import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { format, isSameDay } from "date-fns";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useSearch } from "../contexts/SearchContext";

interface LostTimeDetailItem {
  Type: string;
  Cause: string;
  Object: string;
  Responsible: string;
  Depth: number;
  Summary: string;
  CumHrs: number;
  From: string;
  Hrs: number;
  LtId: number;
  ParentLtId: number;
  Rig: string;
  Date: string;
  Well: string;
  FileUrl: string;
  [key: string]: any;
}

interface Column {
  name: string;
  width?: string;
  flex?: number;
}

interface LostTimeDetailsTableProps {
  searchTerm?: string;
  highlightTerm?: string;
  hideSearch?: boolean;
}

type SortDirection = "asc" | "desc" | null;

interface SortState {
  column: string | null;
  direction: SortDirection;
}

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const tokenizeSearch = (value: string): string[] => {
  const trimmed = (value || "").trim().toLowerCase();
  if (!trimmed) return [];
  const tokens: string[] = [];
  const regex = /\"([^\"]+)\"|([^\s]+)/g; // "phrase" or singleWord
  let match: RegExpExecArray | null;
  while ((match = regex.exec(trimmed)) !== null) {
    const phrase = (match[1] || match[2] || "").trim();
    if (phrase.length > 0) tokens.push(phrase);
  }
  return tokens;
};

const HighlightText = ({
  text,
  searchTerm,
}: {
  text: string | number;
  searchTerm: string;
}) => {
  if (!text) return <>{text}</>;
  const tokens = tokenizeSearch(searchTerm);
  if (tokens.length === 0) return <>{text}</>;

  const pattern = new RegExp(`(${tokens.map(escapeRegex).join("|")})`, "gi");
  const parts = String(text).split(pattern);
  const tokenSet = new Set(tokens.map((t) => t.toLowerCase()));

  return (
    <>
      {parts.map((part, i) => {
        const lower = part.toLowerCase();
        if (tokenSet.has(lower)) {
          return (
            <span
              key={i}
              style={{
                backgroundColor: "#ff9800",
                color: "#000",
                padding: "0 2px",
                borderRadius: "2px",
                fontWeight: "bold",
              }}
            >
              {part}
            </span>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

const LostTimeDetailsTable: React.FC<LostTimeDetailsTableProps> = ({
  searchTerm: externalSearchTerm,
  highlightTerm,
  hideSearch = false,
}) => {
  const {
    searchTerm,
    setSearchTerm,
    pendingSearchTerm,
    setPendingSearchTerm,
    selectedDate,
    setSelectedDate,
    triggerSearch,
    lostTimeDetails,
    loading,
    error,
    availableLocations,
    selectedLocation,
    setSelectedLocation,
    locationLoading,
  } = useSearch();

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [lostTimeDetailsData, setLostTimeDetailsData] = useState<{
    columns: Column[];
    rows: LostTimeDetailItem[];
  }>({ columns: [], rows: [] });
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });
  const searchPerformedRef = useRef(false);

  const handleDateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date || new Date());
    handleDateClose();
    // Always trigger search when date changes
    triggerSearch();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value || "";
    setPendingSearchTerm(value);
  };

  const handleSearchKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      console.log("🔍 Enter pressed, searching for:", pendingSearchTerm);
      setSearchTerm(pendingSearchTerm);
      setActiveSearchTerm(pendingSearchTerm);
      searchPerformedRef.current = false;
      triggerSearch();
    }
  };

  const handleClearSearch = () => {
    setPendingSearchTerm("");
    setSearchTerm("");
    setActiveSearchTerm("");
    searchPerformedRef.current = false;
    triggerSearch();
  };

  const handleSort = (column: string) => {
    setSortState((prev) => {
      if (prev.column === column) {
        // Cycle through: asc → desc → null
        if (prev.direction === "asc") {
          return { column, direction: "desc" };
        } else if (prev.direction === "desc") {
          return { column: null, direction: null };
        } else {
          return { column, direction: "asc" };
        }
      } else {
        // New column, start with asc
        return { column, direction: "asc" };
      }
    });
  };

  const open = Boolean(anchorEl);

  // Get current page data with sorting
  const { sortedLostTimeDetails, currentPageData } = useMemo(() => {
    let sorted = [...lostTimeDetails];

    // Apply sorting if a column is selected
    if (sortState.column && sortState.direction) {
      sorted.sort((a, b) => {
        const aValue = a[sortState.column!]?.toString().toLowerCase() || "";
        const bValue = b[sortState.column!]?.toString().toLowerCase() || "";

        if (sortState.direction === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
    }

    const startIndex = page * rowsPerPage;
    const pageData = sorted.slice(startIndex, startIndex + rowsPerPage);

    return { sortedLostTimeDetails: sorted, currentPageData: pageData };
  }, [lostTimeDetails, page, rowsPerPage, sortState]);

  useEffect(() => {
    const lostTimeColumns: Column[] = [
      { name: "Date", width: "110px" },
      { name: "Rig", width: "100px" },
      { name: "Well", width: "150px" },
      { name: "From", width: "180px" },
      { name: "Hrs", width: "80px" },
      { name: "CumHrs", width: "80px" },
      { name: "LtId", width: "80px" },
      //{ name: 'Type', width: '120px' },
      //{ name: 'Cause', width: '120px' },
      //{ name: 'Object', width: '120px' },
      { name: "Responsible", width: "120px" },
      { name: "Depth", width: "80px" },
      { name: "Summary", flex: 1 },
      { name: "Actions", width: "120px" },
    ];

    setLostTimeDetailsData({ columns: lostTimeColumns, rows: lostTimeDetails });
  }, [lostTimeDetails]);

  // Update active search term when search term changes
  useEffect(() => {
    if (searchTerm && !searchPerformedRef.current) {
      setActiveSearchTerm(searchTerm);
      searchPerformedRef.current = true;
    } else if (!searchTerm) {
      setActiveSearchTerm("");
      searchPerformedRef.current = false;
    }
  }, [searchTerm]);

  // Reset page when sorting changes
  useEffect(() => {
    setPage(0);
  }, [sortState]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <div>
      {!hideSearch && (
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Box sx={{ pt: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by multiple words (use quotes for phrases)"
                  value={pendingSearchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyPress}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {loading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchTerm(pendingSearchTerm);
                              setActiveSearchTerm(pendingSearchTerm);
                              searchPerformedRef.current = false;
                              triggerSearch();
                            }}
                          >
                            <SearchIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: activeSearchTerm
                        ? "rgba(255, 152, 0, 0.1)"
                        : "inherit",
                      "&:hover": {
                        backgroundColor: activeSearchTerm
                          ? "rgba(255, 152, 0, 0.15)"
                          : "inherit",
                      },
                      height: "56px",
                    },
                  }}
                />
                {activeSearchTerm && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearSearch}
                    sx={{
                      mt: 0.5,
                      color: "text.secondary",
                      "&:hover": {
                        color: "primary.main",
                      },
                    }}
                  >
                    Clear search
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleDateClick}
                  startIcon={<CalendarMonthIcon />}
                  sx={{
                    flex: 1,
                    height: "56px",
                    backgroundColor: !isSameDay(selectedDate, new Date())
                      ? "rgba(255, 152, 0, 0.1)"
                      : "inherit",
                    "&:hover": {
                      backgroundColor: !isSameDay(selectedDate, new Date())
                        ? "rgba(255, 152, 0, 0.15)"
                        : "inherit",
                    },
                  }}
                >
                  {format(selectedDate, "yyyy-MM-dd")}
                </Button>
                {availableLocations.length > 1 && (
                  <FormControl sx={{ flex: 1, minWidth: 120 }}>
                    <InputLabel id="location-select-label">Location</InputLabel>
                    <Select
                      labelId="location-select-label"
                      value={selectedLocation || ""}
                      label="Location"
                      onChange={(event) =>
                        setSelectedLocation(event.target.value || null)
                      }
                      startAdornment={<LocationOnIcon sx={{ mr: 1 }} />}
                      sx={{
                        height: "56px",
                        backgroundColor: selectedLocation
                          ? "rgba(255, 152, 0, 0.1)"
                          : "inherit",
                        "&:hover": {
                          backgroundColor: selectedLocation
                            ? "rgba(255, 152, 0, 0.15)"
                            : "inherit",
                        },
                      }}
                      disabled={locationLoading}
                    >
                      {availableLocations.map((location) => (
                        <MenuItem key={location} value={location}>
                          {location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                {!isSameDay(selectedDate, new Date()) && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setSelectedDate(new Date());
                      triggerSearch();
                    }}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        color: "primary.main",
                      },
                    }}
                  >
                    Clear date
                  </Button>
                )}
                {availableLocations.length > 1 && selectedLocation && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setSelectedLocation(availableLocations[0])}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        color: "primary.main",
                      },
                    }}
                  >
                    Reset location
                  </Button>
                )}
              </Box>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleDateClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
              >
                <DayPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  styles={{
                    caption: { color: "white" },
                    day_selected: { backgroundColor: "white", color: "black" },
                    day_today: { color: "#4caf50" },
                    day: { margin: 0 },
                    nav_button: { color: "white" },
                    nav_button_previous: { color: "white" },
                    nav_button_next: { color: "white" },
                  }}
                />
              </Popover>
            </Grid>
          </Grid>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          {lostTimeDetails.length === 0 && searchTerm ? (
            <Alert severity="info">No reports found</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {lostTimeDetailsData.columns.map((column: Column) => (
                      <TableCell
                        key={column.name}
                        style={{
                          width: column.width,
                          cursor:
                            column.name === "Rig" || column.name === "Well"
                              ? "pointer"
                              : "default",
                          userSelect: "none",
                        }}
                        onClick={() => {
                          if (column.name === "Rig" || column.name === "Well") {
                            handleSort(column.name);
                          }
                        }}
                        sx={{
                          "&:hover":
                            column.name === "Rig" || column.name === "Well"
                              ? {
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                }
                              : {},
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {column.name}
                          {(column.name === "Rig" ||
                            column.name === "Well") && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: "20px",
                              }}
                            >
                              {sortState.column === column.name &&
                                sortState.direction === "asc" && (
                                  <ArrowUpwardIcon
                                    sx={{ fontSize: "16px", color: "#1976d2" }}
                                  />
                                )}
                              {sortState.column === column.name &&
                                sortState.direction === "desc" && (
                                  <ArrowDownwardIcon
                                    sx={{ fontSize: "16px", color: "#1976d2" }}
                                  />
                                )}
                              {(sortState.column !== column.name ||
                                sortState.direction === null) && (
                                <Box
                                  sx={{
                                    fontSize: "14px",
                                    opacity: 0.3,
                                    fontWeight: "normal",
                                  }}
                                >
                                  ↕
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentPageData.map(
                    (detail: LostTimeDetailItem, index: number) => (
                      <TableRow key={index}>
                        {lostTimeDetailsData.columns.map((column: Column) => {
                          const value = detail[column.name];
                          return (
                            <TableCell key={column.name}>
                              {column.name === "Actions" ? (
                                detail.FileUrl ? (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    href={detail.FileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      fontSize: "0.75rem",
                                      textTransform: "none",
                                      backgroundColor: "transparent",
                                      color: "white",
                                      "& .MuiSvgIcon-root": {
                                        color: "white",
                                      },
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.1)",
                                      },
                                    }}
                                  >
                                    Open Report
                                  </Button>
                                ) : null
                              ) : (
                                <HighlightText
                                  text={value?.toString() || ""}
                                  searchTerm={activeSearchTerm}
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={sortedLostTimeDetails.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[100]}
              />
            </TableContainer>
          )}
        </>
      )}
    </div>
  );
};

export default LostTimeDetailsTable;
